import { chakra, Spinner, type HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef, type ReactNode } from 'react';
import {
  buttonVariant,
  focusRing,
  squareSize,
  type ButtonVariant,
  type ControlSize,
} from './_shared';

const Base = chakra('button', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontFamily: 'body',
    borderRadius: 'md',
    cursor: 'pointer',
    transitionProperty: 'background-color, border-color, color, filter',
    transitionDuration: 'fast',
    transitionTimingFunction: 'standard',
    _disabled: { opacity: 0.45, cursor: 'not-allowed', pointerEvents: 'none' },
    ...focusRing,
  },
});

export interface IconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'aria-label'> {
  /** Required — icon-only controls must be labelled for screen readers. */
  'aria-label': string;
  icon: ReactNode;
  variant?: ButtonVariant;
  size?: ControlSize;
  loading?: boolean;
}

/** Square, icon-only action. Enforces an accessible label at the type level. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, variant = 'ghost', size = 'md', loading = false, disabled, type = 'button', ...rest },
  ref,
) {
  const sx = { ...squareSize[size], ...buttonVariant[variant] } as HTMLChakraProps<'button'>;
  return (
    <Base
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...sx}
      {...rest}
    >
      {loading ? <Spinner boxSize="1.1em" borderWidth="2px" color="currentColor" /> : icon}
    </Base>
  );
});
